import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  BarChart3, 
  TrendingUp, 
  Phone, 
  Mail, 
  Target,
  Users,
  Trophy,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowUp,
  ArrowDown,
  Calendar,
  Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [callTrends, setCallTrends] = useState([]);
  const [callOutcomes, setCallOutcomes] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [selectedDays, setSelectedDays] = useState("30");

  useEffect(() => {
    fetchAllData();
  }, [selectedDays]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, funnelRes, trendsRes, outcomesRes, leaderRes, pipelineRes] = await Promise.all([
        fetch(`${API}/analytics/overview`, { credentials: "include" }),
        fetch(`${API}/analytics/funnel`, { credentials: "include" }),
        fetch(`${API}/analytics/call-trends?days=${selectedDays}`, { credentials: "include" }),
        fetch(`${API}/analytics/call-outcomes`, { credentials: "include" }),
        fetch(`${API}/analytics/rep-leaderboard`, { credentials: "include" }),
        fetch(`${API}/analytics/pipeline`, { credentials: "include" })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (funnelRes.ok) {
        const data = await funnelRes.json();
        setFunnel(data.funnel || []);
      }
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setCallTrends(data.trends || []);
      }
      if (outcomesRes.ok) {
        const data = await outcomesRes.json();
        setCallOutcomes(data.outcomes || []);
      }
      if (leaderRes.ok) {
        const data = await leaderRes.json();
        setLeaderboard(data.leaderboard || []);
      }
      if (pipelineRes.ok) {
        const data = await pipelineRes.json();
        setPipeline(data);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  // Calculate max for charts
  const maxCalls = Math.max(...callTrends.map(t => t.total_calls), 1);
  const maxFunnel = Math.max(...funnel.map(f => f.count), 1);
  const totalOutcomes = callOutcomes.reduce((sum, o) => sum + o.count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Analytics Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Track performance, conversions, and pipeline health
          </p>
        </div>
        <Select value={selectedDays} onValueChange={setSelectedDays}>
          <SelectTrigger className="w-40">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Leads</p>
                <p className="text-3xl font-bold">{overview?.totals?.leads || 0}</p>
                <p className="text-xs text-emerald-600 flex items-center mt-1">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  {overview?.this_week?.leads || 0} this week
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-3xl font-bold">{overview?.totals?.calls || 0}</p>
                <p className="text-xs text-emerald-600 flex items-center mt-1">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  {overview?.this_week?.calls || 0} this week
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <Phone className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Emails Sent</p>
                <p className="text-3xl font-bold">{overview?.totals?.emails || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Mail className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Conversion Rate</p>
                <p className="text-3xl font-bold">{overview?.conversion?.rate || 0}%</p>
                <p className="text-xs text-slate-500 mt-1">
                  {overview?.conversion?.won || 0} won / {(overview?.conversion?.won || 0) + (overview?.conversion?.lost || 0)} closed
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="funnel" className="space-y-4">
        <TabsList>
          <TabsTrigger value="funnel">Conversion Funnel</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline Health</TabsTrigger>
          <TabsTrigger value="calls">Call Analytics</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        {/* Conversion Funnel Tab */}
        <TabsContent value="funnel">
          <Card>
            <CardHeader>
              <CardTitle>Lead Conversion Funnel</CardTitle>
              <CardDescription>Track leads through each stage of your sales process</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {funnel.map((stage, index) => (
                  <div key={stage.status} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: stage.color }}
                        />
                        <span className="font-medium">{stage.name}</span>
                      </div>
                      <span className="font-bold">{stage.count}</span>
                    </div>
                    <div className="relative h-8 bg-slate-100 rounded-lg overflow-hidden">
                      <div 
                        className="absolute left-0 top-0 h-full rounded-lg transition-all duration-500"
                        style={{ 
                          width: `${(stage.count / maxFunnel) * 100}%`,
                          backgroundColor: stage.color
                        }}
                      />
                      {index < funnel.length - 1 && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                          {funnel[index + 1] && stage.count > 0 
                            ? `${Math.round((funnel[index + 1].count / stage.count) * 100)}% →`
                            : ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Health Tab */}
        <TabsContent value="pipeline">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pipeline Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Follow-up Pipeline
                </CardTitle>
                <CardDescription>Leads categorized by time since last contact</CardDescription>
              </CardHeader>
              <CardContent>
                {pipeline?.pipeline && (
                  <div className="space-y-4">
                    {Object.entries(pipeline.pipeline).map(([key, data]) => (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: data.color }}
                            />
                            <span className="font-medium">{data.name}</span>
                          </div>
                          <Badge 
                            style={{ backgroundColor: data.color + "20", color: data.color }}
                          >
                            {data.count} leads
                          </Badge>
                        </div>
                        <Progress 
                          value={(data.count / (pipeline?.summary?.total_active || 1)) * 100}
                          className="h-2"
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                {pipeline?.summary?.needs_attention > 0 && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">
                        {pipeline.summary.needs_attention} leads need attention
                      </span>
                    </div>
                    <p className="text-sm text-amber-600 mt-1">
                      These leads haven&apos;t been contacted in over 30 days
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Overdue Leads */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Overdue Leads (60+ days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {pipeline?.pipeline?.overdue?.leads?.length > 0 ? (
                    pipeline.pipeline.overdue.leads.slice(0, 10).map((lead) => (
                      <div key={lead.lead_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{lead.business_name}</p>
                          <p className="text-xs text-slate-500">
                            {lead.days_since_contact} days since contact
                          </p>
                        </div>
                        <Button size="sm" variant="outline" asChild>
                          <a href={`/leads/${lead.lead_id}`}>View</a>
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                      <p>No overdue leads! Great job!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Call Analytics Tab */}
        <TabsContent value="calls">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Call Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Call Volume Trend</CardTitle>
                <CardDescription>Daily call activity over the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-end gap-1">
                  {callTrends.slice(-14).map((day, index) => (
                    <div key={day._id} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className="w-full bg-primary rounded-t transition-all hover:bg-primary/80"
                        style={{ 
                          height: `${(day.total_calls / maxCalls) * 200}px`,
                          minHeight: day.total_calls > 0 ? "4px" : "0"
                        }}
                        title={`${day.total_calls} calls`}
                      />
                      <span className="text-[10px] text-slate-500 -rotate-45">
                        {formatDate(day._id)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Call Outcomes Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Call Outcomes</CardTitle>
                <CardDescription>Distribution of call results</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {callOutcomes.map((outcome) => (
                    <div key={outcome.outcome} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: outcome.color }}
                          />
                          <span className="text-sm font-medium">{outcome.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {totalOutcomes > 0 ? Math.round((outcome.count / totalOutcomes) * 100) : 0}%
                          </span>
                          <span className="font-bold">{outcome.count}</span>
                        </div>
                      </div>
                      <Progress 
                        value={totalOutcomes > 0 ? (outcome.count / totalOutcomes) * 100 : 0}
                        className="h-2"
                      />
                    </div>
                  ))}
                  
                  {callOutcomes.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Phone className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No calls logged yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Sales Rep Leaderboard
              </CardTitle>
              <CardDescription>Top performers this month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {leaderboard.map((rep, index) => (
                  <div 
                    key={rep.user_id}
                    className={`flex items-center gap-4 p-4 rounded-lg ${
                      index === 0 ? "bg-amber-50 border border-amber-200" :
                      index === 1 ? "bg-slate-100" :
                      index === 2 ? "bg-orange-50" :
                      "bg-white border"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? "bg-amber-500 text-white" :
                      index === 1 ? "bg-slate-400 text-white" :
                      index === 2 ? "bg-orange-400 text-white" :
                      "bg-slate-200 text-slate-600"
                    }`}>
                      {index + 1}
                    </div>
                    
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={rep.picture} />
                      <AvatarFallback>{getInitials(rep.name)}</AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <p className="font-medium">{rep.name}</p>
                      <p className="text-xs text-slate-500">Score: {rep.score}</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-emerald-600">{rep.deals_won}</p>
                        <p className="text-xs text-slate-500">Deals Won</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-blue-600">{rep.calls_this_month}</p>
                        <p className="text-xs text-slate-500">Calls</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-purple-600">{rep.emails_sent}</p>
                        <p className="text-xs text-slate-500">Emails</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {leaderboard.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No team members yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
